import _ from "lodash";
import cx from "classnames";

import { useSelector } from "react-redux";
import Actions from "src/components/actionbar";
import React, { useContext, useEffect, useMemo } from "react";

import styles from "./menu.module.scss";
import Slider from "src/components/slider";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { SimulationContext } from "../../edit/context";
import { deleteSimulation, discardSimulationDraft, invalidateSimulationDeletionState, invalidateSimulationUpdateState } from "src/redux/actions/coaching/simulation";

export interface MenuState {
  primaryAction: 'save' | 'publish' | null;
  secondaryAction: 'grid' | 'list' | 'generate-questions' | null;
}

interface SimulationMenuBarProps {
  simulation: Simulation;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;
  simulationUpdated: boolean;
}

export const SimulationMenuBar: React.FC<SimulationMenuBarProps> = ({ simulation, menuState, onMenuStateChange, simulationUpdated }) => {
  const courseContext = useContext(SimulationContext);
  const id = courseContext?.id;
  const navigate = useNavigate();

  const [showValidationErrorDialog, setShowValidationErrorDialog] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = React.useState(false);
  
  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(state, `coaching.simulations.update.['${id}'].updating`,false) as boolean;
    const updated = _.get(state, `coaching.simulations.update.['${id}'].updated`,false) as boolean;

    return { updating, updated };
  });

  const { publishing, published } = useSelector((state: any) => {
    const publishing = _.get(state, `coaching.simulations.publish.publishing`,false) as boolean;
    const published = _.get(state, `coaching.simulations.publish.published`,false) as boolean;

    return { publishing, published };
  });

  const hasChanges = useMemo(() => {
    return _.get(simulation, `hasChanges`) as boolean;
  }, [simulation])

  const errors = useSelector((state: any) => {
    return _.size(_.get(state.coaching, 'simulations.edit.errors'));
  }, _.isEqual);

  useEffect(() => {
    if (updated) {
      console.log('Course updated');
    }
  }, [updated]);

  const handleSave = () => {
    onMenuStateChange({
      ...menuState,
      primaryAction: 'save'
    });
  }

  const handlePublish = () => {
    if(errors > 0){
      setShowValidationErrorDialog(true);
      return;
    }
    
    onMenuStateChange({
      ...menuState,
      primaryAction: 'publish'
    });
  }

  const handleDeleteFinish = () => {
    navigate('/coaching/manage');
    setShowDeleteDialog(false);
  }

  const handleDiscardFinish = () => {
    setShowDiscardDialog(false);
  }

  return (
    <div className={cx(styles.menubar)}>
      <Actions.Container>
        <Actions.Row className={styles.row}>
          <Reveal show={errors > 0}>
            <Actions.Button
              className={cx(styles.iconButton, styles.errors)}
              icon="fa-solid fa-triangle-exclamation"
              width={2}
              title={`${errors}`}
              onClick={() => { setShowValidationErrorDialog(true) }}
            />
          </Reveal>
          <Actions.IconButton
            className={styles.iconButton}
            icon="paperflite-delete"
            tooltip="Delete Simulation"
            onClick={() => setShowDeleteDialog(true)}
          />
          {simulation.status === 'PUBLISHED' &&
            <Actions.Row className={styles.row}>
              <Actions.Button
                className={styles.iconButton}
                width={3}
                icon="paperflite-close"
                title='discard changes'
                onClick={() => setShowDiscardDialog(true)}
              />
            </Actions.Row>
          }
          <Actions.Button
            className={cx(styles.iconButton, styles.save)}
            width={2}
            icon="paperflite-tick-unselected"
            title={updating ? "Saving..." : "Save"}
            disabled={updating || publishing}
            onClick={handleSave}
          />

          <Actions.Button
            className={cx(styles.iconButton, styles.publish)}
            icon="paperflite-rocket"
            title={publishing ? "Publishing..." : "Publish"}
            disabled={!hasChanges || simulationUpdated || updating || publishing}
            onClick={handlePublish}
          />
        </Actions.Row>
      </Actions.Container>

      <ValidationErrorDialog show={showValidationErrorDialog} onClose={() => setShowValidationErrorDialog(false)} />
      <DeleteSimulationDialog
        show={showDeleteDialog}
        simulation={simulation}
        onFinish={handleDeleteFinish}
        onClose={() => setShowDeleteDialog(false)}
      />

      
      <DiscardSimulationDialog
        show={showDiscardDialog}
        simulation={simulation}
        onFinish={handleDiscardFinish}
        onClose={() => setShowDiscardDialog(false)}
      />
    </div>
  )
};

interface ValidationErrorDialogProps {
  show: boolean;
  onClose: () => void;
}

export const ValidationErrorDialog: React.FC<ValidationErrorDialogProps> = ({ show, onClose }) => {
  const simulationContext = useContext(SimulationContext);

  const errors = useSelector((state: any) => {
    return _.get(state, 'coaching.simulations.edit.errors') as SimulationValidationError[];
  }, _.isEqual);

  const sections = useMemo(() => {
    const sections: any[] = [];
    const groupErrors = _.groupBy(errors, 'type');
    _.forIn(groupErrors, (value, key) => {
      const content = value[0];
      const type = content.type;
      switch (type) {
        case 'call-settings': {
          sections.push({
            name: 'Call Settings',
            errors: value
          });
          break;
        }
        case 'organization': {
          sections.push({
            name: `Organization Info`,
            errors: value
          });
          break;
        }
        case 'persona': {
          sections.push({
            name: 'Persona',
            errors: value
          });
          break;
        }
        case 'objectives': {
          sections.push({
            name: 'Objectives',
            errors: value
          });
          break;
        }

        default:
          break;
      }
    });

    return sections;
  }, [errors]);

  const handleFixClicked = (error: SimulationValidationError) => {
    onClose();
    simulationContext.gotoError?.(error);
  }

  return (
    <Slider.Dialog width={"550px"} show={show} onClose={onClose}>
      <Slider.Header title="review errors" message="we failed to publish your simulation. Fix these errors and train again." />
      <Slider.Body>
        <div className={styles.validationsBody}>
          {sections.map((section, index) => {
            return (
              <ValidationSection key={index} name={section.name} errors={section.errors} onFixClicked={handleFixClicked} />
            );
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button onClick={onClose}>Close</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface ValidationSectionProps {
  name: string;
  errors: SimulationValidationError[];
  // eslint-disable-next-line no-unused-vars
  onFixClicked: (error: SimulationValidationError) => void;
}

const ValidationSection: React.FC<ValidationSectionProps> = ({ name, errors, onFixClicked }) => {
  return (
    <div className={styles.validationSection}>
      <div className={styles.title}>{name}</div>
      <div className={styles.errors}>
        {errors.map((error, index) => {
          return (
            <div key={index} className={styles.error} onClick={() => onFixClicked(error)}>
              <div className={styles.message}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                {error.message}
              </div>
              <div className={styles.fix}>
                <i className="paperflite-arrow-up"></i>
                fix now
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.separator}></div>
    </div>
  );
}

interface DeleteSimulationDialogProps {
  show: boolean;
  simulation: Simulation;
  onFinish: () => void;
  onClose: () => void;
}

export const DeleteSimulationDialog: React.FC<DeleteSimulationDialogProps> = ({ show, simulation, onFinish, onClose }) => {
  const dispatch = useDispatch();

  const deleting = useSelector((state: any) => {
    return show && _.get(state.coaching, `simulations.deletion['${simulation?.id}'].deleting`) as boolean;
  });

  const deleted = useSelector((state: any) => {
    return show && _.get(state.coaching, `simulations.deletion['${simulation?.id}'].deleted`) as boolean;
  });

  useEffect(() => {
    if (deleted) {
      dispatch(invalidateSimulationDeletionState(simulation.id!));
      onFinish();
    }
  }, [deleted]);

  const handleConfirm = () => {
    dispatch(deleteSimulation(simulation.id!));
  }

  return (
    <Slider.Dialog width={"380px"} show={show} onClose={onClose}>
      <Slider.Header title="deleting..." message={simulation?.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to delete this simulation? This action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary" icon="paperflite-tick" disabled={deleting} onClick={handleConfirm}>{deleting ? 'Deleting...' : 'Confirm'}</Button>
        <Button disabled={deleting} onClick={onClose}>Cancel</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface DiscardSimulationDialogProps {
  show: boolean;
  simulation: Simulation;
  onFinish: () => void;
  onClose: () => void;
}

const DiscardSimulationDialog: React.FC<DiscardSimulationDialogProps> = ({ show, simulation, onFinish, onClose }) => {
  const dispatch = useDispatch();
  const { updating, updated } = useSelector((state: any) => {
    const updating = _.get(state.coaching, `simulations.update.['${simulation.id}'].updating`) as boolean;
    const updated = _.get(state.coaching, `simulations.update.['${simulation.id}'].updated`) as boolean;

    return { updating, updated };
  });

  useEffect(() => {
    if (updated) {
      dispatch(invalidateSimulationUpdateState());
      onFinish();
    }
  }, [updated]);

  const handleConfirm = () => {
    dispatch(discardSimulationDraft(simulation.id!));
  }

  return (
    <Slider.Dialog width={"380px"} show={show} onClose={onClose}>
      <Slider.Header title="Discarding draft..." message={simulation.name} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to discard changes to this simulation? This action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary" icon="paperflite-tick" disabled={updating} onClick={handleConfirm}>{updating ? 'Discarding...' : 'Confirm'}</Button>
        <Button onClick={onClose} disabled={updating}>Cancel</Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
}